import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { FiMoreHorizontal, FiUser } from "react-icons/fi";

interface ProfileAccountInviteProps {
  isLoggedIn: boolean;
  isLoadingCustomer: boolean;
  customerFirstName?: string;
  customerPhotoUrl?: string | null;
  customerAreaHref: string;
  loginHref: string;
  signupHref: string;
  onLogout: () => void;
}

const ACTION_CLASS =
  "inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-xl px-3 text-sm font-semibold text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus-visible:ring-4 focus-visible:ring-primary-100";

function AccountAvatar({ photoUrl }: { photoUrl: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      aria-hidden="true"
      data-testid="profile-account-avatar"
      className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-slate-500"
    >
      {photoUrl && !failed ? (
        // Foto dinâmica da conta, sem processamento pelo servidor de imagens.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photoUrl}
          alt=""
          width={32}
          height={32}
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <FiUser data-testid="profile-account-avatar-fallback" className="h-4 w-4" />
      )}
    </span>
  );
}

export function ProfileAccountInvite({
  isLoggedIn,
  isLoadingCustomer,
  customerFirstName,
  customerPhotoUrl,
  customerAreaHref,
  loginHref,
  signupHref,
  onLogout,
}: ProfileAccountInviteProps) {
  const menu = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (menu.current && !menu.current.contains(event.target as Node))
        menu.current.open = false;
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, []);
  return (
    <section
      aria-label="Conta do cliente"
      data-testid="profile-account-invite"
      className="flex min-h-[44px] min-w-0 items-center justify-between gap-2 px-1 text-sm text-slate-600"
    >
      {isLoadingCustomer ? (
        <p role="status" className="py-3 text-xs text-slate-500">
          Verificando sua conta...
        </p>
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-2">
            {isLoggedIn && (
              <AccountAvatar
                key={customerPhotoUrl?.trim() || ""}
                photoUrl={customerPhotoUrl?.trim() || ""}
              />
            )}
            <p className="min-w-0 truncate">
              {isLoggedIn ? `Olá, ${customerFirstName || "cliente"}` : "Sua conta"}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              href={isLoggedIn ? customerAreaHref : loginHref}
              data-testid={isLoggedIn ? "profile-account-area" : "profile-account-login"}
              className={ACTION_CLASS}
            >
              {isLoggedIn ? "Minha conta" : "Entrar"}
            </Link>
            <details
              ref={menu}
              className="relative"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node))
                  event.currentTarget.open = false;
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.currentTarget.removeAttribute("open");
                  event.currentTarget.querySelector("summary")?.focus();
                }
              }}
            >
              <summary
                aria-label="Mais opções da conta"
                className="inline-flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 focus-visible:ring-4 focus-visible:ring-primary-100"
              >
                <FiMoreHorizontal aria-hidden="true" />
              </summary>
              <div className="absolute right-0 top-full z-20 min-w-[160px] rounded-xl border border-slate-200/80 bg-white p-1 shadow-subtle">
                {isLoggedIn ? (
                  <button
                    type="button"
                    data-testid="profile-account-logout"
                    className={`${ACTION_CLASS} w-full text-rose-700 hover:bg-rose-50`}
                    onClick={onLogout}
                  >
                    Sair da conta
                  </button>
                ) : (
                  <Link
                    href={signupHref}
                    data-testid="profile-account-signup"
                    className={`${ACTION_CLASS} w-full`}
                  >
                    Criar conta
                  </Link>
                )}
              </div>
            </details>
          </div>
        </>
      )}
    </section>
  );
}
